import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Pagination,
  Typography,
  useTheme,
} from "@mui/material";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SPONSOR_COACH_ID_STORAGE_KEY, useAuthUser } from "../../Context/AuthUserContext";
import { getCamiCodesListWithPaginationByFilter } from "../../Repository/Api";
import {
  buildWatchUrl,
  countVideosInGroups,
  enrichVideosWithTimestamps,
  findLatestVideoInGroup,
  groupListRowsByCamiCode,
} from "../../Repository/CamiCodeVideosApi";

const PAGE_SIZE = 10;

function formatDuration(seconds) {
  if (seconds == null || seconds === "") return null;
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return null;
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  return `${secs}s`;
}

function formatDate(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function resolveListFilterId(profile) {
  try {
    const fromLs = localStorage.getItem(SPONSOR_COACH_ID_STORAGE_KEY);
    if (fromLs != null && fromLs !== "") {
      const n = Number(fromLs);
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch {
    /* ignore */
  }
  const coachId = Number(profile?.coachId);
  if (Number.isFinite(coachId) && coachId > 0) return coachId;
  const userId = Number(profile?.id);
  return Number.isFinite(userId) && userId > 0 ? userId : 0;
}

export default function MyMatchList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const mainColor = theme.palette.primary.main;
  const { profileReady, isLoggedIn, profile, refreshProfile } = useAuthUser();

  const highlightCamiCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("c") || "").replace(/ /g, "");
  }, [location.search]);

  const highlightVideoId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("v") || "").replace(/ /g, "");
  }, [location.search]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pendingCoachRefresh, setPendingCoachRefresh] = useState(false);
  const coachRefreshAttempted = useRef(false);

  const filterId = resolveListFilterId(profile);

  useEffect(() => {
    if (!profileReady || !isLoggedIn) return;
    const fid = resolveListFilterId(profile);
    if (fid > 0 || coachRefreshAttempted.current) return;
    coachRefreshAttempted.current = true;
    setPendingCoachRefresh(true);
    refreshProfile()
      .catch(() => {})
      .finally(() => setPendingCoachRefresh(false));
  }, [profileReady, isLoggedIn, profile, refreshProfile]);

  const loadPage = useCallback(async (pageNumber) => {
      const fid = resolveListFilterId(profile);
      if (fid === 0) {
        setError(null);
        setGroups([]);
        setTotalPages(0);
        setTotalItems(0);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getCamiCodesListWithPaginationByFilter(pageNumber, PAGE_SIZE, fid, "");
        const rows = Array.isArray(data?.list) ? data.list : [];
        const grouped = groupListRowsByCamiCode(rows);
        const enriched = await Promise.all(
          grouped.map(async (group) => {
            const videos = await enrichVideosWithTimestamps(group.videos || []);
            return {
              ...group,
              videos,
              latestVideo: videos[0] ?? null,
            };
          })
        );
        setGroups(enriched);
        setTotalPages(Math.max(1, Number(data?.totalPages) || 1));
        setTotalItems(countVideosInGroups(enriched));
      } catch (e) {
        setError(e?.message || "Could not load videos.");
        setGroups([]);
        setTotalPages(0);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [profile]
  );

  useEffect(() => {
    if (!profileReady || !isLoggedIn) return;
    if (resolveListFilterId(profile) === 0) return;
    loadPage(page);
  }, [profileReady, isLoggedIn, page, loadPage, profile]);

  if (!profileReady) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (pendingCoachRefresh || (filterId === 0 && !coachRefreshAttempted.current)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (filterId === 0) {
    return (
      <Box sx={{ px: { xs: 1.6, sm: 2.5 }, py: 3 }}>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.95rem" }}>
          No videos are available for your account yet. Scan a QR code or try again later.
        </Typography>
      </Box>
    );
  }

  const goWatch = (group, video) => {
    const c = group?.camiCode;
    const p = group?.uniqueId;
    const videoId = video?.videoId;
    if (!c || !p) return;
    const redir = encodeURIComponent("/my-match-list");
    navigate(
      buildWatchUrl({
        camiCode: c,
        uniqueId: p,
        videoId: videoId || undefined,
        redir,
        playConfirmed: true,
      })
    );
  };

  const showVideoCount = !loading && !(error && groups.length === 0);

  return (
    <Box sx={{ px: { xs: 1.6, sm: 2.5 }, py: { xs: 1.4, sm: 2.2 }, minHeight: "calc(100vh - 260px)" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          columnGap: 1.5,
          rowGap: 0.5,
          mb: 2,
          textAlign: "center",
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            color: mainColor,
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
            m: 0,
          }}
        >
          videos
        </Typography>
        {showVideoCount ? (
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: { xs: "0.9rem", sm: "0.95rem" } }}>
            {totalItems} video{totalItems === 1 ? "" : "s"}
          </Typography>
        ) : null}
      </Box>

      {loading && groups.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error && groups.length === 0 ? (
        <Typography sx={{ color: "error.main", fontSize: "0.95rem" }}>{error}</Typography>
      ) : (
        <>
          {error && groups.length > 0 ? (
            <Typography sx={{ color: "error.main", fontSize: "0.9rem", mb: 1 }}>{error}</Typography>
          ) : null}

          <Card
            elevation={2}
            sx={{
              borderRadius: 2.4,
              border: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: "#fff",
              width: "100%",
              maxWidth: 720,
              mx: "auto",
            }}
          >
            <CardContent sx={{ px: 2.4, py: 1.7, "&:last-child": { pb: 1.7 } }}>
              {loading && groups.length > 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : null}
              {!loading && groups.length === 0 ? (
                <Typography sx={{ color: theme.palette.text.secondary, py: 2 }}>No videos yet.</Typography>
              ) : (
                groups.map((group) => {
                  const isHighlighted =
                    highlightCamiCode && group.camiCode === highlightCamiCode;
                  const groupTitle =
                    group.title || group.gameName || group.camiCode || "—";
                  const videoCount = group.videos?.length || 0;
                  const latestVideo = findLatestVideoInGroup(group);
                  const playLatestVideo = () => {
                    if (latestVideo) {
                      goWatch(group, latestVideo);
                    }
                  };

                  return (
                    <Box
                      key={`${group.camiCode}::${group.uniqueId}`}
                      sx={{
                        py: 1.4,
                        borderTop: `1px solid ${theme.palette.grey[200]}`,
                        "&:first-of-type": { borderTop: "none", pt: 0 },
                        borderLeft: isHighlighted ? `3px solid ${theme.palette.secondary.main}` : "none",
                        pl: isHighlighted ? 1 : 0,
                      }}
                    >
                      <Typography
                        component="button"
                        type="button"
                        onClick={playLatestVideo}
                        disabled={!latestVideo}
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.02rem",
                          color: mainColor,
                          mb: 0.5,
                          display: "block",
                          textAlign: "left",
                          border: "none",
                          background: "none",
                          fontFamily: "inherit",
                          p: 0,
                          cursor: latestVideo ? "pointer" : "default",
                          opacity: latestVideo ? 1 : 0.7,
                        }}
                      >
                        {groupTitle}
                      </Typography>
                      <Typography
                        component="button"
                        type="button"
                        onClick={playLatestVideo}
                        disabled={!latestVideo}
                        sx={{
                          fontSize: "0.85rem",
                          color: theme.palette.text.secondary,
                          mb: 1,
                          display: "block",
                          textAlign: "left",
                          border: "none",
                          background: "none",
                          fontFamily: "inherit",
                          p: 0,
                          cursor: latestVideo ? "pointer" : "default",
                          opacity: latestVideo ? 1 : 0.7,
                        }}
                      >
                        {group.camiCode}
                        {videoCount > 1 ? ` · ${videoCount} videos` : ""}
                      </Typography>
                      {(group.videos || []).map((video, index) => {
                        const isCurrentVideo =
                          highlightVideoId && video.videoId === highlightVideoId;
                        const canPlay = !!(group.camiCode && group.uniqueId && video.videoId);
                        const videoLabel =
                          video.title ||
                          (videoCount > 1 ? `Video ${index + 1}` : "Play");
                        const timingLabel = [formatDate(video.createdAt), formatDuration(video.duration)]
                          .filter(Boolean)
                          .join(" · ");

                        return (
                          <Box
                            key={video.videoId || index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              py: 0.9,
                              borderTop: index > 0 ? `1px solid ${theme.palette.grey[100]}` : "none",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0, flex: 1 }}>
                              <PlayCircleFilledIcon sx={{ color: mainColor, fontSize: "1.6rem", flexShrink: 0 }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontWeight: isCurrentVideo ? 700 : 600,
                                    fontSize: "0.98rem",
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  {videoLabel}
                                </Typography>
                                {timingLabel ? (
                                  <Typography
                                    sx={{
                                      fontSize: "0.82rem",
                                      color: theme.palette.text.secondary,
                                      mt: 0.2,
                                    }}
                                  >
                                    {timingLabel}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                            <Typography
                              component="button"
                              type="button"
                              disabled={!canPlay}
                              onClick={() => goWatch(group, video)}
                              sx={{
                                color: mainColor,
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                flexShrink: 0,
                                cursor: canPlay ? "pointer" : "not-allowed",
                                opacity: canPlay ? 1 : 0.45,
                                border: "none",
                                background: "none",
                                fontFamily: "inherit",
                                p: 0,
                                textDecoration: "underline",
                              }}
                            >
                              Play
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>

          {totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
              <Pagination
                color="primary"
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                disabled={loading}
                size="large"
              />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
}
